import { FormControl, InputGroup, OverlayTrigger, Popover, Glyphicon } from 'react-bootstrap';
import { ReImburseTableWrapper } from '../../styles'
import { IWatrLimit, IWatrRequestTravels } from '@nkr/models/index';
import { useState, FormEvent, useEffect, useMemo } from 'react';
import { IPriorTotals } from '../prior-totals';
import { Override } from './override';
import { getExpenseTypeOrder, ExpenseType } from './constants/expense-types';
import { sortLineItemsBySummaryOrder } from './utils/expense.utils';
import { getDefaultMileageRate } from './constants/reimbursement';
import { formatMoneyDisplay, formatMoneyInput, parseMoneyInput, sanitizeMoneyInputString } from '../common/money-input';

const requestedInputStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#212529',
    opacity: 1
};

const checkboxStyle = (checked: boolean, disabled: boolean = false) => ({
    cursor: disabled ? 'not-allowed' as const : 'pointer' as const,
    width: '20px',
    height: '20px',
    border: disabled ? '2px solid #ced4da' : '2px solid #ffc107',
    borderRadius: '4px',
    backgroundColor: disabled ? '#e9ecef' : (checked ? '#ffc107' : 'transparent'),
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    position: 'relative' as const,
    opacity: disabled ? 0.6 : 1,
    ...(checked && !disabled && {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '12px 12px'
    })
});

const overrideCheckboxStyle = (checked: boolean) => ({
    cursor: 'pointer' as const,
    width: '20px',
    height: '20px',
    border: checked ? '2px solid #0d6efd' : '2px solid #ced4da',
    borderRadius: '4px',
    backgroundColor: checked ? '#0d6efd' : 'white',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    position: 'relative' as const,
    margin: 0,
    pointerEvents: 'auto' as const,
    opacity: 1,
    ...(checked && {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '12px 12px'
    })
});

interface ILineItem {
    id: number;
    entityType: number;
    entityTypeId: number;
    expenseType: number;
    description?: string;
    amount: number;
    receiptFileId?: number;
    createdDateTime: string;
    lastUpdated?: string;
    reviewRequired: boolean;
    expenseDate?: string;
    receiptFileName?: string;
    isCaregiverExpense?: boolean;
    numberOfNights?: number;
}

interface IProps {
    data: IWatrRequestTravels,
    limit: IWatrLimit,
    lineItems: ILineItem[],
    activeExpenseType?: number | null,
    approvedExpenseTypes?: Set<number>,
    approvedLineItemIds?: Set<number>,
    activeExpenseTypeFileIndex?: Map<number, number>,
    onOpenMileageReview?: () => void | Promise<void>,
    totalReimbursementAmount: number,
    totalMilesReimbursementAmountWithOverride: number,
    totalMealsReimbursementAmountWithOverride: number,
    totalCarServiceExpenseReimbursementAmountWithOverride: number,
    totalDependentCarExpenseReimbursementAmountWithOverride: number,
    requestedGrandTotal: number,
    approvedGrandTotal: number,
    effectiveDerReimbursementLimit?: number,
    priorTotals: IPriorTotals,
    isReadOnly?: boolean,
    viewType?: 'trip' | 'overall',
    tripId?: number | null,
    allTripIds?: number[],
    totalOneWayMiles?: number,
    mileageRate?: number,
    onChange: (e: React.FormEvent<HTMLInputElement & FormControl>, id: number) => void,
    onBlur?: (e: React.FocusEvent<HTMLInputElement & FormControl>, id: number) => void,
    onOverride?: (id: number, override: Override) => void,
    onSetReviewRequired?: (lineItemIds: number[], reviewRequired: boolean) => Promise<void>,
    onReviewRequiredStateChange?: (lineItemIds: number[], reviewRequired: boolean) => void,
    reviewRequiredState?: Map<number, boolean>,
    onCaregiverToggle?: (lineItemId: number, isCaregiver: boolean) => void,
    lineItemState?: Map<number, Partial<ILineItem>>,
    onUpdateLineItem?: (lineItemId: number, updates: Partial<ILineItem>) => void,
    hasAdvancedReimbursement?: boolean
}

interface ExpenseTypeConfig {
    expenseType: number;
    label: string;
    inputName: string;
    inputId: string;
    overrideKey: keyof IWatrRequestTravels;
    overrideCheckboxId: string;
    overrideInputId: string;
    overrideInputName: string;
    overrideInputPlaceholder: string;
    overrideEnum?: Override;
    getRequestedAmount: (amounts: ReturnType<typeof aggregateRequestedAmounts>) => number;
    getLimit: (limit: IWatrLimit) => string | number;
    getLimitValue: (limit: IWatrLimit) => number;
    getApprovedAmount: (props: IProps) => number;
    getEditingValue: (editing: typeof initialEditing, data: IWatrRequestTravels, requestedAmount: number) => number | string | undefined;
    getOverrideValue: (editing: typeof initialEditing, data: IWatrRequestTravels, requestedAmount: number, isOverridden: boolean) => number | string | undefined;
    hasSpecialStyling?: boolean;
    getSpecialStyle?: (requestedAmount: number, limit: IWatrLimit) => React.CSSProperties;
    relatedExpenseTypes?: number[];
}

const aggregateRequestedAmounts = (lineItems: ILineItem[]) => {
    return {
        lodging: lineItems.filter(li => li.expenseType === ExpenseType.LODGING).reduce((sum, li) => sum + li.amount, 0),
        flight: lineItems.filter(li => li.expenseType === ExpenseType.FLIGHT).reduce((sum, li) => sum + li.amount, 0),
        transportation: lineItems.filter(li => li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE).reduce((sum, li) => sum + li.amount, 0),
        meals: lineItems.filter(li => li.expenseType === ExpenseType.MEALS).reduce((sum, li) => sum + li.amount, 0),
        dependentCare: lineItems.filter(li => li.expenseType === ExpenseType.DEPENDENT_CARE).reduce((sum, li) => sum + li.amount, 0)
    };
};

const calculateLodgingApprovedAmount = (lineItems: ILineItem[], requestedAmount: number, maxNights: number): number => {
    const lodgingLineItems = lineItems.filter(li => li.expenseType === ExpenseType.LODGING);
    const totalNights = lodgingLineItems.reduce((sum, li) => sum + (li.numberOfNights || 0), 0);
    
    if (totalNights > maxNights && totalNights > 0) {
        return Math.round((requestedAmount / totalNights) * maxNights * 100) / 100;
    }
    
    return requestedAmount;
};

const expenseTypeConfigs: ExpenseTypeConfig[] = [
    {
        expenseType: ExpenseType.LODGING,
        label: 'Lodging',
        inputName: 'hotelLodgingExpense',
        inputId: 'hotelLodgingExpense',
        overrideKey: 'isHotelLodgingExpenseOverridden' as keyof IWatrRequestTravels,
        overrideCheckboxId: 'isHotelLodgingExpenseOverridden',
        overrideInputId: 'overrideHotelLodgingExpense',
        overrideInputName: 'overrideHotelLodgingExpense',
        overrideInputPlaceholder: 'Enter Lodging Expense',
        overrideEnum: Override.Lodging,
        getRequestedAmount: (amounts) => amounts.lodging,
        getLimit: (limit) => `${limit.hotelNights} nights`,
        getLimitValue: () => 0,
        getApprovedAmount: (props) => {
            const requested = props.lineItems.filter(li => li.expenseType === ExpenseType.LODGING).reduce((sum, li) => sum + li.amount, 0);
            if (props.viewType === 'overall' && (props.data as any).isHotelLodgingExpenseOverridden) {
                const overrideValue = (props.data as any).overrideHotelLodgingExpense;
                if (overrideValue > 0) {
                    return Math.ceil(overrideValue);
                }
            }
            const approved = calculateLodgingApprovedAmount(props.lineItems, requested, props.limit.hotelNights);
            return Math.ceil(approved);
        },
        getEditingValue: (editing, data, requestedAmount) => editing.hotelLodgingExpense ? requestedAmount : (requestedAmount > 0 ? requestedAmount : undefined),
        getOverrideValue: (editing, data, requestedAmount, isOverridden) => {
            if (!isOverridden) return undefined;
            const overrideValue = (data as any).overrideHotelLodgingExpense;
            if (editing.overrideHotelLodgingExpense) {
                return overrideValue != null && overrideValue > 0 ? overrideValue : (requestedAmount > 0 ? requestedAmount : undefined);
            }
            if (overrideValue != null && overrideValue > 0) {
                return overrideValue;
            }
            return requestedAmount > 0 ? requestedAmount : undefined;
        }
    },
    {
        expenseType: ExpenseType.FLIGHT,
        label: 'Flight(s)',
        inputName: 'flightAndTrainExpense',
        inputId: 'flightAndTrainExpense',
        overrideKey: 'isFlightAndTrainExpenseOverridden' as keyof IWatrRequestTravels,
        overrideCheckboxId: '',
        overrideInputId: '',
        overrideInputName: '',
        overrideInputPlaceholder: '',
        getRequestedAmount: (amounts) => amounts.flight,
        getLimit: (limit) => `${limit.roundTrips} Round Trips`,
        getLimitValue: () => 0,
        getApprovedAmount: (props) => {
            const requested = props.lineItems.filter(li => li.expenseType === ExpenseType.FLIGHT).reduce((sum, li) => sum + li.amount, 0);
            return Math.ceil(requested);
        },
        getEditingValue: (editing, data, requestedAmount) => editing.flightAndTrainExpense ? requestedAmount : (requestedAmount > 0 ? requestedAmount : undefined),
        getOverrideValue: () => undefined
    },
    {
        expenseType: ExpenseType.TRANSPORTATION,
        label: 'Transportation',
        inputName: 'carServiceExpense',
        inputId: 'carServiceExpense',
        overrideKey: 'isCarServiceExpenseOverridden' as keyof IWatrRequestTravels,
        overrideCheckboxId: 'isCarServiceExpenseOverriden',
        overrideInputId: 'overrideCarServiceExpense',
        overrideInputName: 'overrideCarServiceExpense',
        overrideInputPlaceholder: 'Enter Transportation Expenses',
        overrideEnum: Override.carService,
        getRequestedAmount: (amounts) => amounts.transportation,
        getLimit: (limit) => formatMoneyDisplay(limit.transportationExpenses),
        getLimitValue: (limit) => limit.transportationExpenses,
        getApprovedAmount: (props) => {
            if (props.viewType === 'overall' && props.data.isCarServiceExpenseOverridden) {
                const overrideValue = props.data.overrideCarServiceExpense;
                if (overrideValue > 0) {
                    return Math.ceil(overrideValue);
                }
            }
            let amount = props.totalCarServiceExpenseReimbursementAmountWithOverride;
            if (!amount || amount === 0) {
                const transportationLineItems = props.lineItems.filter(li => li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE);
                amount = transportationLineItems.reduce((sum, li) => sum + li.amount, 0);
            }
            const limit = props.limit.transportationExpenses;
            return Math.ceil(Math.min(amount, limit));
        },
        getEditingValue: (editing, data, requestedAmount) => editing.carServiceExpense ? requestedAmount : (requestedAmount > 0 ? requestedAmount : undefined),
        getOverrideValue: (editing, data, requestedAmount, isOverridden) => {
            if (!isOverridden) return undefined;
            if (editing.overrideCarServiceExpense) {
                return data.overrideCarServiceExpense != null && data.overrideCarServiceExpense > 0 ? data.overrideCarServiceExpense : (requestedAmount > 0 ? requestedAmount : undefined);
            }
            if (data.overrideCarServiceExpense != null && data.overrideCarServiceExpense > 0) {
                return data.overrideCarServiceExpense;
            }
            return requestedAmount > 0 ? requestedAmount : undefined;
        },
        hasSpecialStyling: true,
        getSpecialStyle: (requestedAmount, limit) => ({ ...requestedInputStyle, color: requestedAmount > limit.transportationExpenses ? '#dc3546' : '#212529' }),
        relatedExpenseTypes: [6]
    },
    {
        expenseType: ExpenseType.DEPENDENT_CARE,
        label: 'Dependent Care',
        inputName: 'dependentCareExpense',
        inputId: 'dependentCareExpense',
        overrideKey: 'isDependentCareExpenseOverridden' as keyof IWatrRequestTravels,
        overrideCheckboxId: 'isDependentCareExpenseOverridden',
        overrideInputId: 'overrideDependentCareExpense',
        overrideInputName: 'overrideDependentCareExpense',
        overrideInputPlaceholder: 'Enter Dependent Care Expense',
        overrideEnum: Override.DependentCare,
        getRequestedAmount: (amounts) => amounts.dependentCare,
        getLimit: (limit) => formatMoneyDisplay(limit.dependentCare),
        getLimitValue: (limit) => limit.dependentCare,
        getApprovedAmount: (props) => {
            if (props.viewType === 'overall' && props.data.isDependentCareExpenseOverridden) {
                const overrideValue = props.data.overrideDependentCareExpense;
                if (overrideValue > 0) {
                    return Math.ceil(overrideValue);
                }
            }
            const amount = props.totalDependentCarExpenseReimbursementAmountWithOverride;
            const limit = props.limit.dependentCare;
            return Math.ceil(Math.min(amount, limit));
        },
        getEditingValue: (editing, data, requestedAmount) => editing.dependentCareExpense ? requestedAmount : (requestedAmount > 0 ? requestedAmount : undefined),
        getOverrideValue: (editing, data, requestedAmount, isOverridden) => {
            if (!isOverridden) return undefined;
            if (editing.overrideDependentCareExpense) {
                return data.overrideDependentCareExpense != null && data.overrideDependentCareExpense > 0 ? data.overrideDependentCareExpense : (requestedAmount > 0 ? requestedAmount : undefined);
            }
            if (data.overrideDependentCareExpense != null && data.overrideDependentCareExpense > 0) {
                return data.overrideDependentCareExpense;
            }
            return requestedAmount > 0 ? requestedAmount : undefined;
        },
        hasSpecialStyling: true,
        getSpecialStyle: (requestedAmount, limit) => ({ ...requestedInputStyle, color: requestedAmount > limit.dependentCare ? '#dc3546' : '#212529' })
    },
    {
        expenseType: ExpenseType.MEALS,
        label: 'Meals',
        inputName: 'mealExpense',
        inputId: 'mealExpense',
        overrideKey: 'isMealsOverWritten' as keyof IWatrRequestTravels,
        overrideCheckboxId: 'isMealsOverWritten',
        overrideInputId: 'overrideMeals',
        overrideInputName: 'overrideMeals',
        overrideInputPlaceholder: 'Enter Meal Expense',
        overrideEnum: Override.Meals,
        getRequestedAmount: (amounts) => amounts.meals,
        getLimit: (limit) => formatMoneyDisplay(limit.meals),
        getLimitValue: (limit) => limit.meals,
        getApprovedAmount: (props) => {
            if (props.viewType === 'overall' && props.data.isMealsOverWritten) {
                const overrideValue = props.data.overrideMeals;
                if (overrideValue > 0) {
                    return Math.ceil(overrideValue);
                }
            }
            const amount = props.totalMealsReimbursementAmountWithOverride;
            const limit = props.limit.meals;
            return Math.ceil(Math.min(amount, limit));
        },
        getEditingValue: (editing, data, requestedAmount) => editing.mealExpense ? requestedAmount : (requestedAmount > 0 ? requestedAmount : undefined),
        getOverrideValue: (editing, data, requestedAmount, isOverridden) => {
            if (!isOverridden) return undefined;
            if (editing.overrideMeals) {
                return data.overrideMeals != null && data.overrideMeals > 0 ? data.overrideMeals : (requestedAmount > 0 ? requestedAmount : undefined);
            }
            if (data.overrideMeals != null && data.overrideMeals > 0) {
                return data.overrideMeals;
            }
            return requestedAmount > 0 ? requestedAmount : undefined;
        }
    }
];

const initialEditing = {
    roundTripMiles: false,
    overrideMiles: false,
    hotelLodgingExpense: false,
    carServiceExpense: false,
    mealExpense: false,
    overrideMeals: false,
    flightAndTrainExpense: false,
    dependentCareExpense: false,
    overrideDependentCareExpense: false,
    overrideCarServiceExpense: false,
    overrideHotelLodgingExpense: false,
};

const DonorExpenseRequestSummary = (props: IProps) => {
    const [editing, setEditing] = useState(initialEditing);
    const [reviewRequiredState, setReviewRequiredState] = useState<Map<number, boolean>>(new Map());
    const [overrideInputValues, setOverrideInputValues] = useState<Map<string, number | string>>(new Map());
    const [editingLineItemId, setEditingLineItemId] = useState<number | null>(null);
    const [lineItemAmountDraft, setLineItemAmountDraft] = useState('');

    useEffect(() => {
        if (props.reviewRequiredState) {
            const mergedState = new Map<number, boolean>();
            props.lineItems.forEach(li => {
                const propValue = props.reviewRequiredState!.get(li.id);
                mergedState.set(li.id, propValue !== undefined ? propValue : li.reviewRequired);
            });
            setReviewRequiredState(mergedState);
        } else {
            const newState = new Map<number, boolean>();
            props.lineItems.forEach(li => {
                const existingValue = reviewRequiredState.get(li.id);
                newState.set(li.id, existingValue !== undefined ? existingValue : li.reviewRequired);
            });
            setReviewRequiredState(newState);
        }
    }, [props.lineItems, props.reviewRequiredState]);

    const requestedAmounts = useMemo(() => aggregateRequestedAmounts(props.lineItems || []), [props.lineItems]);

    const resolveLineItemAmount = (lineItem: ILineItem): number => {
        const state = props.lineItemState ? props.lineItemState.get(lineItem.id) : undefined;

        if (state && state.amount !== undefined) {
            return state.amount;
        }

        return lineItem.amount;
    };

    const requestedTotalDisplay = useMemo(() => {
        return (props.lineItems || []).reduce((sum, li) => sum + resolveLineItemAmount(li), 0);
    }, [props.lineItems, props.lineItemState]);

    const getCaregiverAmount = (expenseType: number): number => {
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        const expenseTypes = config?.relatedExpenseTypes ? [expenseType, ...config.relatedExpenseTypes] : [expenseType];
        const lineItemsForType = props.lineItems.filter(li => expenseTypes.includes(li.expenseType));
        
        if (lineItemsForType.length === 0) return 0;
        
        return lineItemsForType.reduce((sum, li) => {
            const state = props.lineItemState ? props.lineItemState.get(li.id) : undefined;
            const isCaregiver = state && state.isCaregiverExpense !== undefined
                ? state.isCaregiverExpense
                : (li.isCaregiverExpense || false);
            const amount = state && state.amount !== undefined ? state.amount : li.amount;

            return isCaregiver ? sum + amount : sum;
        }, 0);
    };

    const showOverride = props.viewType === 'overall';
    const showLimit = props.viewType === 'overall';

    const caregiverGrandTotal = useMemo(() => {
        return expenseTypeConfigs.reduce((sum, config) => {
            return sum + getCaregiverAmount(config.expenseType);
        }, 0);
    }, [props.lineItems, props.lineItemState]);

    const mileageRate = props.mileageRate ?? getDefaultMileageRate();
    const sumBeforeOverallCap = useMemo(() => {
        const mileageLimit = props.limit.mileage * mileageRate;
        const mileageApproved = Math.min(props.totalMilesReimbursementAmountWithOverride, mileageLimit);
        const lodgingRequested = requestedAmounts.lodging;
        let lodgingApproved = calculateLodgingApprovedAmount(props.lineItems, lodgingRequested, props.limit.hotelNights);
        if (props.viewType === 'overall' && (props.data as any).isHotelLodgingExpenseOverridden && (props.data as any).overrideHotelLodgingExpense > 0) {
            lodgingApproved = (props.data as any).overrideHotelLodgingExpense;
        }
        const flightRequested = requestedAmounts.flight;
        const flightApproved = flightRequested;
        const transportationApproved = props.viewType === 'overall' && props.data.isCarServiceExpenseOverridden && props.data.overrideCarServiceExpense > 0
            ? props.data.overrideCarServiceExpense
            : Math.min(props.totalCarServiceExpenseReimbursementAmountWithOverride, props.limit.transportationExpenses);
        const mealsApproved = props.viewType === 'overall' && props.data.isMealsOverWritten && props.data.overrideMeals > 0
            ? props.data.overrideMeals
            : Math.min(props.totalMealsReimbursementAmountWithOverride, props.limit.meals);
        const dependentCareApproved = props.viewType === 'overall' && props.data.isDependentCareExpenseOverridden && props.data.overrideDependentCareExpense > 0
            ? props.data.overrideDependentCareExpense
            : Math.min(props.totalDependentCarExpenseReimbursementAmountWithOverride, props.limit.dependentCare);
        
        return mileageApproved + lodgingApproved + flightApproved + transportationApproved + mealsApproved + dependentCareApproved;
    }, [
        props.totalMilesReimbursementAmountWithOverride,
        props.limit.mileage,
        mileageRate,
        requestedAmounts.lodging,
        requestedAmounts.flight,
        props.totalCarServiceExpenseReimbursementAmountWithOverride,
        props.limit.transportationExpenses,
        props.totalMealsReimbursementAmountWithOverride,
        props.limit.meals,
        props.totalDependentCarExpenseReimbursementAmountWithOverride,
        props.limit.dependentCare,
        props.lineItems,
        props.limit.hotelNights,
        props.viewType,
        props.data,
        props.data.isMealsOverWritten,
        props.data.overrideMeals,
        props.data.isCarServiceExpenseOverridden,
        props.data.overrideCarServiceExpense,
        props.data.isDependentCareExpenseOverridden,
        props.data.overrideDependentCareExpense
    ]);

    const effectiveDerReimbursementLimit = props.effectiveDerReimbursementLimit ?? props.limit.derReimbursementLimit;

    const calculatedApprovedGrandTotal = useMemo(() => {
        const remainingDerLimit = Math.max(0, effectiveDerReimbursementLimit - props.priorTotals.der);
        return Math.min(sumBeforeOverallCap, remainingDerLimit);
    }, [
        sumBeforeOverallCap,
        effectiveDerReimbursementLimit,
        props.priorTotals.der
    ]);

    const availableExpenseTypes = useMemo(() => {
        const expenseTypeSet = new Set<number>();
        props.lineItems.forEach(li => {
            if (li.expenseType === ExpenseType.MILEAGE) {
                return;
            }

            if (li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE) {
                expenseTypeSet.add(ExpenseType.TRANSPORTATION);
            } else {
                expenseTypeSet.add(li.expenseType);
            }
        });
        
        return expenseTypeConfigs
            .filter(config => {
                if (config.expenseType === ExpenseType.TRANSPORTATION) {
                    return expenseTypeSet.has(ExpenseType.TRANSPORTATION) || expenseTypeSet.has(ExpenseType.CAR_SERVICE);
                }
                return expenseTypeSet.has(config.expenseType);
            })
            .sort((a, b) => {
                const orderA = getExpenseTypeOrder(a.expenseType);
                const orderB = getExpenseTypeOrder(b.expenseType);
                return orderA - orderB;
            });
    }, [props.lineItems]);

    const isReviewRequired = (expenseType: number) => {
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        const expenseTypes = config?.relatedExpenseTypes ? [expenseType, ...config.relatedExpenseTypes] : [expenseType];
        const lineItemsForType = props.lineItems.filter(li => expenseTypes.includes(li.expenseType));
        
        return lineItemsForType.some(li => {
            if (props.reviewRequiredState && props.reviewRequiredState.has(li.id)) {
                return props.reviewRequiredState.get(li.id) === true;
            }
            const stateValue = reviewRequiredState.get(li.id);
            return stateValue !== undefined ? stateValue : li.reviewRequired;
        });
    };

    const isMileageReviewRequired = (): boolean => {
        const mileageLineItems = props.lineItems.filter(li => li.expenseType === ExpenseType.MILEAGE);
        if (mileageLineItems.length === 0) return false;
        
        const stateToCheck = props.reviewRequiredState || reviewRequiredState;
        return mileageLineItems.some(li => {
            const stateValue = stateToCheck.get(li.id);
            return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
        });
    };

    const handleReviewRequiredChange = async (expenseType: number, checked: boolean) => {
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        const expenseTypes = config?.relatedExpenseTypes ? [expenseType, ...config.relatedExpenseTypes] : [expenseType];
        const lineItemIds = props.lineItems
            .filter(li => expenseTypes.includes(li.expenseType))
                .map(li => li.id);
        
        if (props.onReviewRequiredStateChange) {
            props.onReviewRequiredStateChange(lineItemIds, checked);
        } else {
            setReviewRequiredState(prev => {
                const newState = new Map(prev);
                lineItemIds.forEach(id => {
                    newState.set(id, checked);
                });
                return newState;
            });
        }
        
        if (props.onSetReviewRequired && lineItemIds.length > 0) {
            try {
                await props.onSetReviewRequired(lineItemIds, checked);
            } catch (error) {
            }
        }
    };

    const handleLineItemReviewRequiredChange = async (lineItemId: number, checked: boolean) => {
        if (props.onReviewRequiredStateChange) {
            props.onReviewRequiredStateChange([lineItemId], checked);
        } else {
            setReviewRequiredState(prev => {
                const newState = new Map(prev);
                newState.set(lineItemId, checked);
                return newState;
            });
        }

        if (props.onSetReviewRequired) {
            try {
                await props.onSetReviewRequired([lineItemId], checked);
            } catch (error) {
            }
        }
    };

    const isLineItemReviewRequired = (lineItem: ILineItem): boolean => {
        if (props.reviewRequiredState && props.reviewRequiredState.has(lineItem.id)) {
            return props.reviewRequiredState.get(lineItem.id) === true;
        }
        if (reviewRequiredState.has(lineItem.id)) {
            return reviewRequiredState.get(lineItem.id) === true;
        }
        return lineItem.reviewRequired === true;
    };

    const getOrderedReceiptFileIds = (lineItemsForType: ILineItem[]): number[] => {
        const files: number[] = [];
        const seen: { [id: number]: boolean } = {};
        lineItemsForType.forEach(li => {
            if (li.receiptFileId && !seen[li.receiptFileId]) {
                seen[li.receiptFileId] = true;
                files.push(li.receiptFileId);
            }
        });
        return files;
    };

    const renderLineItemExpenseRow = (
        config: ExpenseTypeConfig,
        lineItem: ILineItem,
        itemIndex: number,
        totalItems: number,
        activeFileId: number | null,
        lineItemsForType: ILineItem[]
    ) => {
        const reviewRequired = isLineItemReviewRequired(lineItem);
        const amount = resolveLineItemAmount(lineItem);
        const caregiverState = props.lineItemState ? props.lineItemState.get(lineItem.id) : undefined;
        const isCaregiver = caregiverState && caregiverState.isCaregiverExpense !== undefined
            ? caregiverState.isCaregiverExpense
            : !!lineItem.isCaregiverExpense;
        const caregiverAmount = isCaregiver ? amount : 0;
        const isEditingAmount = editingLineItemId === lineItem.id;
        const amountValue = isEditingAmount
            ? lineItemAmountDraft
            : formatRequestedValue(amount, false);
        const firstIndexForActiveFile = activeFileId == null
            ? -1
            : lineItemsForType.findIndex(li => li.receiptFileId === activeFileId);
        const isActive = isRowActive(config.expenseType)
            && !!lineItem.receiptFileId
            && activeFileId === lineItem.receiptFileId
            && firstIndexForActiveFile === itemIndex;
        const isApproved = isRowApproved(config.expenseType)
            || !!(props.approvedLineItemIds && props.approvedLineItemIds.has(lineItem.id));
        const itemBadge = isActive
            ? (totalItems > 1 ? `Viewing ${itemIndex + 1} of ${totalItems}` : 'Viewing')
            : (totalItems > 1 ? `Item ${itemIndex + 1} of ${totalItems}` : '');
        const rowStyle = getRowStyle(config.expenseType, reviewRequired, { ignoreTypeActive: true });
        if (isActive) {
            rowStyle.borderLeft = '4px solid #0d6efd';
            rowStyle.backgroundColor = '#d1ecf1';
            rowStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
        } else if (isApproved) {
            rowStyle.borderLeft = '4px solid #28a745';
            rowStyle.backgroundColor = '#d4edda';
            rowStyle.boxShadow = undefined;
        } else {
            rowStyle.borderLeft = '4px solid #ffc107';
            rowStyle.backgroundColor = '#fff3cd';
            rowStyle.boxShadow = undefined;
        }

        return (
            <tr key={`${config.expenseType}-${lineItem.id}`} style={rowStyle}>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={reviewRequired}
                        disabled={props.isReadOnly || props.viewType === 'overall'}
                        onChange={(e) => handleLineItemReviewRequiredChange(lineItem.id, e.target.checked)}
                        style={checkboxStyle(reviewRequired, props.isReadOnly || props.viewType === 'overall')}
                    />
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{config.label}</span>
                        {itemBadge && (
                            <span style={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: isActive ? '#0d6efd' : '#6c757d',
                                padding: '2px 6px',
                                borderRadius: 4,
                                backgroundColor: isActive ? '#e7f1ff' : '#f8f9fa',
                                border: isActive ? '1px solid #0d6efd' : '1px solid #dee2e6',
                                whiteSpace: 'nowrap'
                            }}>
                                {itemBadge}
                            </span>
                        )}
                    </div>
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'right' }}>
                    {caregiverAmount > 0 ? formatMoneyDisplay(caregiverAmount) : '--'}
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', minWidth: '180px' }}>
                    <div className="input-group input-group-sm">
                        <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>$</span>
                        <FormControl
                            type="text"
                            value={amountValue}
                            disabled={props.isReadOnly}
                            onFocus={() => {
                                setEditingLineItemId(lineItem.id);
                                setLineItemAmountDraft(String(amount));
                            }}
                            onChange={(e: FormEvent<HTMLInputElement & FormControl>) => {
                                const next = sanitizeMoneyInputString(e.currentTarget.value);

                                setLineItemAmountDraft(next);

                                if (props.onUpdateLineItem) {
                                    props.onUpdateLineItem(lineItem.id, { amount: parseMoneyInput(next) });
                                }
                            }}
                            onBlur={() => {
                                const nextAmount = Math.round(parseMoneyInput(lineItemAmountDraft) * 100) / 100;

                                if (props.onUpdateLineItem) {
                                    props.onUpdateLineItem(lineItem.id, { amount: nextAmount });
                                }

                                setEditingLineItemId(null);
                                setLineItemAmountDraft('');
                            }}
                            style={requestedInputStyle}
                        />
                    </div>
                </td>
            </tr>
        );
    };

    const handleMileageReviewRequiredChange = async (checked: boolean) => {
        const mileageLineItems = props.lineItems.filter(li => li.expenseType === ExpenseType.MILEAGE);
        const lineItemIds = mileageLineItems.map(li => li.id);
        
        if (props.onReviewRequiredStateChange) {
            props.onReviewRequiredStateChange(lineItemIds, checked);
        } else {
            setReviewRequiredState(prev => {
                const newState = new Map(prev);
                lineItemIds.forEach(id => {
                    newState.set(id, checked);
                });
                return newState;
            });
        }
        
        if (props.onSetReviewRequired && lineItemIds.length > 0) {
            try {
                await props.onSetReviewRequired(lineItemIds, checked);
            } catch (error) {
            }
        }
    };

    const handleCaregiverToggle = (expenseType: number, checked: boolean) => {
        if (!props.onCaregiverToggle) return;
        
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        const expenseTypes = config?.relatedExpenseTypes ? [expenseType, ...config.relatedExpenseTypes] : [expenseType];
        const lineItemsForType = props.lineItems.filter(li => expenseTypes.includes(li.expenseType));
        
        lineItemsForType.forEach(li => {
            props.onCaregiverToggle!(li.id, checked);
        });
    };

    const hasValueForExpenseType = (expenseType: number): boolean => {
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        if (!config) return false;
        const requestedAmount = config.getRequestedAmount(requestedAmounts);
        return requestedAmount > 0;
    };

    const hasSelfDrivingMileageValue = (): boolean => {
        return (Number(props.totalReimbursementAmount) || 0) > 0
            || (Number(props.totalOneWayMiles) || 0) > 0;
    };

    const isRowActive = (expenseType: number | string): boolean => {
        if (props.activeExpenseType === null || props.activeExpenseType === undefined) return false;
        const activeType = typeof expenseType === 'string' ? null : expenseType;
        if (activeType === null) return false;
        
        const normalizedActiveType = props.activeExpenseType === 6 ? 3 : props.activeExpenseType;
        
        if (normalizedActiveType === activeType) {
            return true;
        }
        
        const config = expenseTypeConfigs.find(c => c.expenseType === activeType);
        if (config?.relatedExpenseTypes) {
            if (config.relatedExpenseTypes.includes(props.activeExpenseType) || 
                (props.activeExpenseType === 6 && config.relatedExpenseTypes.includes(3)) ||
                (props.activeExpenseType === 3 && config.relatedExpenseTypes.includes(6))) {
                return true;
            }
        }
        
        return false;
    };

    const isRowApproved = (expenseType: number | string): boolean => {
        if (!props.approvedExpenseTypes || props.approvedExpenseTypes.size === 0) return false;
        const approvedType = typeof expenseType === 'string' ? null : expenseType;
        if (approvedType === null) return false;
        
        if (props.approvedExpenseTypes.has(approvedType)) {
            return true;
        }
        
        const config = expenseTypeConfigs.find(c => c.expenseType === approvedType);
        if (config?.relatedExpenseTypes) {

            if (config.relatedExpenseTypes.some(et => props.approvedExpenseTypes!.has(et))) {
                return true;
            }
        }
        
        if (approvedType === 3 && props.approvedExpenseTypes.has(6)) {
            return true;
        }
        
        if (approvedType === 6 && props.approvedExpenseTypes.has(3)) {
            return true;
        }
        
        return false;
    };

    const getRowStyle = (
        expenseType: number | string,
        _hasReviewRequired: boolean,
        options?: { ignoreTypeActive?: boolean }
    ) => {
        const baseStyle: React.CSSProperties = {
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.2s ease, border-left 0.2s ease, box-shadow 0.2s ease'
        };

        const isActive = options && options.ignoreTypeActive
            ? false
            : (expenseType === 'mileage'
                ? props.activeExpenseType === ExpenseType.MILEAGE
                : isRowActive(expenseType));
        const isApproved = expenseType === 'mileage'
            ? isRowApproved(ExpenseType.MILEAGE)
            : isRowApproved(expenseType);

        if (isActive) {
            baseStyle.borderLeft = '4px solid #0d6efd';
            baseStyle.backgroundColor = '#d1ecf1';
            baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
        } else if (isApproved) {
            baseStyle.borderLeft = '4px solid #28a745';
            baseStyle.backgroundColor = '#d4edda';
        } else {
            baseStyle.borderLeft = '4px solid #ffc107';
            baseStyle.backgroundColor = '#fff3cd';
        }

        return baseStyle;
    };

    const handleInputChange = (e: FormEvent<HTMLInputElement & FormControl>) => {
        const target = e.currentTarget;
        const name = target.name;
        let value = target.value;
        
        if (name === 'roundTripMiles' || name === 'hotelLodgingExpense' || name === 'flightAndTrainExpense' || 
            name === 'carServiceExpense' || name === 'mealExpense' || name === 'dependentCareExpense') {
            const numericValue = value.replace(/,/g, '');
            if (numericValue === '' || /^-?\d+$/.test(numericValue)) {
                const roundedValue = numericValue === '' ? '' : Math.round(parseFloat(numericValue)).toString();
                const syntheticEvent = {
                    ...e,
                    currentTarget: {
                        ...target,
                        value: roundedValue
                    }
                } as FormEvent<HTMLInputElement & FormControl>;
                props.onChange(syntheticEvent, props.data.id);
            }
            return;
        }
        
        if (name === 'overrideMiles' || name === 'overrideMeals' || name === 'overrideCarServiceExpense' || 
            name === 'overrideDependentCareExpense' || name === 'overrideHotelLodgingExpense') {
            setOverrideInputValues(prev => {
                const newMap = new Map(prev);
                newMap.set(name, value);
                return newMap;
            });
        } else {
            props.onChange(e, props.data.id);
        }
    };

    const handleOverrideMiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onOverride(props.data.id, Override.Miles);
    };

    const handleOverride = (overrideEnum: Override) => (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onOverride(props.data.id, overrideEnum);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement & FormControl>) => {
        setEditing({ ...editing, [e.currentTarget.name]: true });
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement & FormControl>) => {
        const name = e.currentTarget.name;
        setEditing({ ...editing, [name]: false });
        
        if (name === 'overrideMiles' || name === 'overrideMeals' || name === 'overrideCarServiceExpense' || 
            name === 'overrideDependentCareExpense' || name === 'overrideHotelLodgingExpense') {
            if (props.onBlur) {
                props.onBlur(e, props.data.id);
            }
            setOverrideInputValues(prev => {
                const newMap = new Map(prev);
                newMap.delete(name);
                return newMap;
            });
        }
    };

    const getLineItemCounts = (expenseType: number) => {
        const config = expenseTypeConfigs.find(c => c.expenseType === expenseType);
        const expenseTypes = config?.relatedExpenseTypes ? [expenseType, ...config.relatedExpenseTypes] : [expenseType];
        const lineItemsForType = sortLineItemsBySummaryOrder(
            props.lineItems.filter(li => expenseTypes.includes(li.expenseType))
        );
        
        const total = lineItemsForType.length;
        
        const uniqueFiles = new Set<number>();
        lineItemsForType.forEach(li => {
            if (li.receiptFileId) {
                uniqueFiles.add(li.receiptFileId);
            }
        });
        const totalFiles = uniqueFiles.size;
        
        let pending = 0;
        lineItemsForType.forEach(li => {
            let isReviewRequired = false;
            if (props.reviewRequiredState && props.reviewRequiredState.has(li.id)) {
                isReviewRequired = props.reviewRequiredState.get(li.id) === true;
            } else {
                const stateValue = reviewRequiredState.get(li.id);
                isReviewRequired = stateValue !== undefined ? stateValue === true : (li.reviewRequired === true);
            }
            if (isReviewRequired) {
                pending++;
            }
        });
        
        return { total, pending, totalFiles, lineItemsForType };
    };

    const formatRequestedValue = (value: number | string | undefined, isEditing: boolean): string => {
        if (value === undefined || value === null || value === '') return '';
        const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(numValue)) return '';
        if (isEditing) return String(numValue);
        return formatMoneyInput(numValue);
    };

    const renderExpenseRow = (config: ExpenseTypeConfig) => {
        const requestedAmount = config.getRequestedAmount(requestedAmounts);
        const hasValue = hasValueForExpenseType(config.expenseType);
        const reviewRequired = config.relatedExpenseTypes 
            ? config.relatedExpenseTypes.some(et => isReviewRequired(et)) || isReviewRequired(config.expenseType)
            : isReviewRequired(config.expenseType);
        const caregiverAmount = getCaregiverAmount(config.expenseType);
        const inputValue = config.getEditingValue(editing, props.data, requestedAmount);
        const isEditingInput = editing[config.inputName as keyof typeof editing] || false;
        const formattedInputValue = formatRequestedValue(inputValue, isEditingInput);
        const isOverridden = props.data[config.overrideKey] as boolean || false;
        let overrideValue = config.getOverrideValue(editing, props.data, requestedAmount, isOverridden);
        
        if (config.overrideInputName && overrideInputValues.has(config.overrideInputName)) {
            const localValue = overrideInputValues.get(config.overrideInputName);
            overrideValue = localValue !== undefined && localValue !== null ? localValue : overrideValue;
        }
        const limitDisplay = config.getLimit(props.limit);
        const limitValue = config.getLimitValue(props.limit);
        const approvedAmount = config.getApprovedAmount(props);
        const isCapped = limitValue > 0 && requestedAmount > limitValue && approvedAmount >= limitValue;
        const inputStyle = config.hasSpecialStyling && config.getSpecialStyle 
            ? config.getSpecialStyle(requestedAmount, props.limit)
            : requestedInputStyle;

        const isActive = isRowActive(config.expenseType);
        const { total, lineItemsForType } = getLineItemCounts(config.expenseType);
        const receiptFileIds = getOrderedReceiptFileIds(lineItemsForType);
        const currentFileIndex = props.activeExpenseTypeFileIndex?.get(config.expenseType) ?? 0;
        const activeFileId = receiptFileIds[Math.min(currentFileIndex, Math.max(receiptFileIds.length - 1, 0))] || null;

        if (props.viewType === 'trip' && lineItemsForType.length > 0) {
            return lineItemsForType.map((lineItem, index) =>
                renderLineItemExpenseRow(
                    config,
                    lineItem,
                    index,
                    lineItemsForType.length,
                    activeFileId,
                    lineItemsForType
                )
            );
        }

        const itemCountLabel = total > 1 ? `${total} items` : (total === 1 ? '1 item' : '');

        const showOverride = props.viewType === 'overall';
        const showLimit = props.viewType === 'overall';

        return (
            <tr key={config.expenseType} style={getRowStyle(config.expenseType, reviewRequired)}>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
                    <input 
                        className="form-check-input" 
                        type="checkbox" 
                        checked={reviewRequired}
                        disabled={props.isReadOnly || !hasValue || props.viewType === 'overall'}
                        onChange={(e) => handleReviewRequiredChange(config.expenseType, e.target.checked)}
                        style={checkboxStyle(reviewRequired, props.isReadOnly || !hasValue || props.viewType === 'overall')} 
                    />
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-start', minWidth: 0 }}>
                        <span style={{ textAlign: 'left', whiteSpace: 'nowrap', flexShrink: 0 }}>{config.label}</span>
                        {itemCountLabel && (
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: isActive
                                    ? '#0d6efd'
                                    : (props.isReadOnly 
                                        ? '#6c757d' 
                                        : (reviewRequired ? '#ffc107' : '#6c757d')),
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: isActive
                                    ? '#e7f1ff'
                                    : (props.isReadOnly 
                                        ? '#f8f9fa' 
                                        : (reviewRequired ? '#fff3cd' : '#f8f9fa')),
                                border: isActive
                                    ? '1px solid #0d6efd'
                                    : (props.isReadOnly 
                                        ? '1px solid #dee2e6' 
                                        : (reviewRequired ? '1px solid #ffc107' : '1px solid #dee2e6')),
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}>
                                {itemCountLabel}
                            </span>
                        )}
                    </div>
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'right' }}>
                    {caregiverAmount > 0 ? formatMoneyDisplay(caregiverAmount) : '--'}
                </td>
                <td style={{ padding: '10px 12px', verticalAlign: 'middle', minWidth: '180px' }}>
                    {config.expenseType === ExpenseType.LODGING && (() => {
                        const lodgingLineItems = props.lineItems.filter(li => li.expenseType === ExpenseType.LODGING);
                        const totalNights = lodgingLineItems.reduce((sum, li) => sum + (li.numberOfNights || 0), 0);
                        const isOverLimit = totalNights > props.limit.hotelNights;
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ fontSize: '12px', color: isOverLimit ? '#dc3545' : '#6c757d', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {totalNights} {totalNights === 1 ? 'night' : 'nights'}
                                    {isOverLimit && ` (Over limit: ${props.limit.hotelNights})`}
                                </div>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>$</span>
                                    <FormControl
                                        id={config.inputId}
                                        name={config.inputName}
                                        type='text'
                                        value={formattedInputValue}
                                        disabled={props.isReadOnly || !hasValue}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        );
                    })()}
                    {config.expenseType !== ExpenseType.LODGING && (
                        <div className="input-group input-group-sm">
                            <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>$</span>
                            <FormControl
                                id={config.inputId}
                                name={config.inputName}
                                type='text'
                                value={formattedInputValue}
                                disabled={props.isReadOnly || !hasValue}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                onChange={handleInputChange}
                                style={inputStyle}
                            />
                        </div>
                    )}
                </td>
                {showLimit && (
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{limitDisplay}</td>
                )}
                {showOverride && (
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                        {config.overrideCheckboxId ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <input
                                    className="form-check-input"
                                    type='checkbox'
                                    id={config.overrideCheckboxId}
                                    name={config.overrideCheckboxId}
                                    checked={isOverridden}
                                    onChange={config.overrideEnum && props.onOverride ? handleOverride(config.overrideEnum) : undefined}
                                    disabled={props.isReadOnly && props.viewType !== 'overall'}
                                    style={overrideCheckboxStyle(isOverridden)}
                                />
                                {isOverridden && (
                                    <div className="input-group input-group-sm" style={{ width: '100px' }}>
                                        <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>$</span>
                                        <FormControl
                                            type='number'
                                            id={config.overrideInputId}
                                            name={config.overrideInputName}
                                            placeholder={config.overrideInputPlaceholder}
                                            disabled={(props.isReadOnly && props.viewType !== 'overall') || !isOverridden}
                                            style={{ color: overrideValue && typeof overrideValue === 'number' && overrideValue > limitValue ? '#dc3546' : '', fontSize: '13px', textAlign: 'right' }}
                                            value={overrideValue != null ? overrideValue : ''}
                                            onFocus={handleFocus}
                                            onBlur={handleBlur}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            '--'
                        )}
                    </td>
                )}
                {showOverride && (
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle', color: isCapped ? '#dc3545' : '' }}>{formatMoneyDisplay(approvedAmount)}</td>
                )}
            </tr>
        );
    };

    const getMileageOverrideValue = (): number | string | undefined => {
        if (!props.data.isMilesOverWritten) return undefined;
        if (overrideInputValues.has('overrideMiles')) {
            return overrideInputValues.get('overrideMiles');
        }
        if (editing.overrideMiles) {
            return props.data.overrideMiles != null && props.data.overrideMiles > 0 ? props.data.overrideMiles : undefined;
        }
        if (props.data.overrideMiles != null && props.data.overrideMiles > 0) {
            return props.data.overrideMiles;
        }
        return undefined;
    };
    
    let overrideMiles = getMileageOverrideValue();
    let grandTotal = effectiveDerReimbursementLimit - props.priorTotals.der;

    const styleIds = availableExpenseTypes.map(config => `#${config.inputId}`).join(', ');

    return (
        <div style={{ background: 'white', borderRadius: '6px', overflow: 'hidden', marginBottom: '16px', border: '1px solid #e1e8ed', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <style>{`
                ${styleIds}[disabled],
                #roundTripMiles[disabled] {
                    opacity: 1 !important;
                    color: #212529 !important;
                    -webkit-text-fill-color: #212529 !important;
                }
            `}</style>
            <div style={{ background: '#6c757d', color: 'white', padding: '10px 16px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
                    <span>Travel Expense Request Summary</span>
                    {props.hasAdvancedReimbursement && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: 12, color: '#fff3cd' }}>
                            <span>Advanced reimbursement on file — check for duplicate receipts</span>
                            <OverlayTrigger
                                trigger={['hover', 'focus', 'click']}
                                placement='bottom'
                                rootClose
                                overlay={
                                    <Popover id='der-advanced-reimbursement-notice' title='Advanced reimbursement'>
                                        <div style={{ fontSize: 13, lineHeight: 1.45, maxWidth: 320 }}>
                                            <p style={{ marginBottom: 8 }}>
                                                This donor has advanced reimbursement activity on their Donor Shield request.
                                            </p>
                                            <p style={{ marginBottom: 8 }}>
                                                Receipts or expenses may already have been funded through advanced reimbursement (for example workup or pre-op card loads).
                                            </p>
                                            <p style={{ marginBottom: 0 }}>
                                                Before approving this DER claim, compare submitted receipts against advanced reimbursement history to avoid paying the same expense twice.
                                            </p>
                                        </div>
                                    </Popover>
                                }
                            >
                                <Glyphicon
                                    glyph='question-sign'
                                    style={{ cursor: 'help', fontSize: 14, color: '#fff', opacity: 0.95 }}
                                    title='More details'
                                />
                            </OverlayTrigger>
                        </span>
                    )}
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <ReImburseTableWrapper bordered style={{ marginBottom: 0, fontSize: '13px', width: '100%' }}>
                    {props.viewType === 'overall' ? (
                        <colgroup>
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '17%' }} />
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '14%' }} />
                        </colgroup>
                    ) : (
                        <colgroup>
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '51%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '17%' }} />
                        </colgroup>
                    )}
                    <thead>
                        <tr>
                            <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '100px', textAlign: 'center' }}>REVIEW REQUIRED</th>
                            <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', textAlign: 'left' }}>EXPENSE TYPE</th>
                            <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '120px', textAlign: 'right' }}>
                                <span title="Expense incurred by caregiver accompanying donor">CAREGIVER</span>
                            </th>
                            <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '140px', textAlign: 'right' }}>REQUESTED TOTAL</th>
                            {props.viewType === 'overall' && (
                                <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '150px' }}>REIMBURSEMENT LIMIT</th>
                            )}
                            {props.viewType === 'overall' && (
                                <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '120px', textAlign: 'center' }}>OVERRIDE</th>
                            )}
                            {props.viewType === 'overall' && (
                                <th style={{ backgroundColor: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: '12px', padding: '10px 12px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', width: '140px', textAlign: 'right' }}>APPROVED</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {availableExpenseTypes.map(config => renderExpenseRow(config)).flat()}
                        {hasSelfDrivingMileageValue() && (
                        <tr
                            style={{
                                borderBottom: '1px solid #f0f0f0',
                                ...getRowStyle('mileage', isMileageReviewRequired()),
                                cursor: props.onOpenMileageReview && props.viewType === 'trip'
                                    ? 'pointer'
                                    : undefined
                            }}
                            onClick={() => {
                                if (props.onOpenMileageReview && hasSelfDrivingMileageValue() && props.viewType === 'trip' && !props.isReadOnly) {
                                    props.onOpenMileageReview();
                                }
                            }}
                        >
                            <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={isMileageReviewRequired()}
                                    disabled={props.isReadOnly || !hasSelfDrivingMileageValue() || props.viewType === 'overall'}
                                    onChange={(e) => handleMileageReviewRequiredChange(e.target.checked)}
                                    style={checkboxStyle(isMileageReviewRequired(), props.isReadOnly || !hasSelfDrivingMileageValue() || props.viewType === 'overall')} 
                                />
                            </td>
                            <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Self-Driving Mileage</span>
                                    {props.activeExpenseType === ExpenseType.MILEAGE && props.viewType === 'trip' && (
                                        <span style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: '#0d6efd',
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            backgroundColor: '#e7f1ff',
                                            border: '1px solid #0d6efd',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            Viewing
                                        </span>
                                    )}
                                    {props.totalOneWayMiles !== undefined && props.totalOneWayMiles > 0 && mileageRate > 0 && (
                                        <OverlayTrigger
                                            trigger={['hover', 'focus', 'click']}
                                            placement='right'
                                            rootClose
                                            overlay={
                                                <Popover id='mileage-calculation-hint' title='Mileage calculation'>
                                                    <div style={{ fontSize: 13, lineHeight: 1.45, maxWidth: 280 }}>
                                                        <p style={{ marginBottom: 8 }}>
                                                            Round-trip miles × IRS mileage rate
                                                        </p>
                                                        <p style={{ marginBottom: 0, fontFamily: 'monospace', fontSize: 12 }}>
                                                            {props.totalOneWayMiles.toLocaleString('en-US')} mi × ${mileageRate.toFixed(2)}/mi = ${Number(props.totalReimbursementAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </p>
                                                    </div>
                                                </Popover>
                                            }
                                        >
                                            <Glyphicon
                                                glyph='info-sign'
                                                style={{ cursor: 'help', fontSize: 13, color: '#6c757d' }}
                                                title='How mileage is calculated'
                                            />
                                        </OverlayTrigger>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'right' }}>
                                --
                            </td>
                            <td style={{ padding: '10px 12px', verticalAlign: 'middle', minWidth: '180px' }} onClick={e => e.stopPropagation()}>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>$</span>
                                    <FormControl
                                        type='text'
                                        id='roundTripMiles'
                                        name='roundTripMiles'
                                        value={props.totalReimbursementAmount > 0 ? formatRequestedValue(props.totalReimbursementAmount, editing.roundTripMiles) : ''}
                                        onChange={handleInputChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        disabled={props.isReadOnly || !hasSelfDrivingMileageValue()}
                                        style={requestedInputStyle}
                                    />
                                </div>
                            </td>
                            {showLimit && (
                                <td style={{ padding: '10px 12px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>{props.limit.mileage.toLocaleString('en-us')} miles</td>
                            )}
                            {showOverride && (
                                <td style={{ padding: '10px 12px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        <input
                                            className="form-check-input"
                                            type='checkbox'
                                            id='isMilesOverWritten'
                                            name='isMilesOverWritten'
                                            onChange={props.onOverride ? handleOverrideMiles : undefined}
                                            checked={props.data.isMilesOverWritten || false}
                                            disabled={props.isReadOnly && props.viewType !== 'overall'}
                                            style={overrideCheckboxStyle(props.data.isMilesOverWritten || false)}
                                        />
                                        {props.data.isMilesOverWritten && (
                                            <div className="input-group input-group-sm" style={{ width: '100px' }}>
                                                <span className="input-group-addon" style={{ fontSize: '13px', backgroundColor: '#f8f9fa', borderColor: '#ced4da' }}>mi</span>
                                                <FormControl
                                                    type='number'
                                                    id='overrideMiles'
                                                    name='overrideMiles'
                                                    placeholder='Enter miles'
                                                    disabled={(props.isReadOnly && props.viewType !== 'overall') || !props.data.isMilesOverWritten}
                                                    style={{ color: props.data.overrideMiles > props.limit.mileage ? '#dc3546' : '', fontSize: '13px', textAlign: 'right' }}
                                                    value={overrideMiles != null ? overrideMiles : ''}
                                                    onFocus={handleFocus}
                                                    onBlur={handleBlur}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </td>
                            )}
                            {showOverride && (() => {
                                const mileageLimit = props.limit.mileage * mileageRate;
                                let mileageApproved: number;
                                if (props.viewType === 'overall' && props.data.isMilesOverWritten && props.data.overrideMiles > 0) {
                                    mileageApproved = props.totalMilesReimbursementAmountWithOverride;
                                } else {
                                    mileageApproved = Math.min(props.totalMilesReimbursementAmountWithOverride, mileageLimit);
                                }
                                const isOverLimit = props.totalMilesReimbursementAmountWithOverride > mileageLimit;
                                return (
                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle', color: isOverLimit ? '#dc3545' : '' }}>
                                        {formatMoneyDisplay(mileageApproved)}
                                    </td>
                                );
                            })()}
                        </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 600 }}>
                            <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6' }}></td>
                            <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6' }}>Total</td>
                            <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6', textAlign: 'right' }}>
                                {caregiverGrandTotal > 0 ? formatMoneyDisplay(caregiverGrandTotal) : '--'}
                            </td>
                            <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6' }}>{formatMoneyDisplay(requestedTotalDisplay)}</td>
                            {showLimit && (
                                <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6' }}>{formatMoneyDisplay(effectiveDerReimbursementLimit)}</td>
                            )}
                            {showOverride && (
                                <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6' }}>--</td>
                            )}
                            {showOverride && (
                                <td style={{ padding: '10px 12px', borderTop: '2px solid #dee2e6', color: sumBeforeOverallCap > grandTotal ? '#dc3545' : '', fontSize: '16px' }}>
                                    <strong>{formatMoneyDisplay(calculatedApprovedGrandTotal)}</strong>
                                </td>
                            )}
                        </tr>
                    </tfoot>
                </ReImburseTableWrapper>
            </div>
        </div>
    );
}

export default DonorExpenseRequestSummary;
